/**
 * Fetches MNIST IDX files from the server and hands the raw bytes to the worker.
 *
 * The bytes are transferred (not copied), so the main thread keeps no reference
 * to them; the parsed dataset lives in the worker.
 */
import type { RawMnist } from '../../shared/mnist/dataset.ts';

async function fetchBytes(url: string): Promise<Uint8Array> {
  const response = await fetch(url);
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Failed to fetch ${url} (${String(response.status)})`);
  }
  return new Uint8Array(await response.arrayBuffer());
}

export interface MnistAvailability {
  available: boolean;
}

export async function checkAvailability(): Promise<MnistAvailability> {
  const response = await fetch('/api/mnist/info');
  if (!response.ok) {
    return { available: false };
  }
  return (await response.json()) as MnistAvailability;
}

export async function loadMnist(): Promise<RawMnist> {
  const [trainImages, trainLabels, testImages, testLabels] = await Promise.all([
    fetchBytes('/api/mnist/train/images'),
    fetchBytes('/api/mnist/train/labels'),
    fetchBytes('/api/mnist/test/images'),
    fetchBytes('/api/mnist/test/labels'),
  ]);

  return { trainImages, trainLabels, testImages, testLabels };
}
